import { Menu, Transition, Popover, Listbox } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon, DotsVerticalIcon, PlusIcon } from '@heroicons/react/outline';
import { Loader } from 'components';
import Avatar from 'components/Avatar';
import { FloatingActionButton, FloatingActionButtons } from 'components/FloatingActionButton';
import NewScoreDialog, { getIntervalDate } from 'components/NewScoreDialog/NewScoreDialog';
import { ErrorToast, SuccessToast } from 'components/Toast';
import { getConditional, UPDATE_MEASURABLE, UPDATE_MEASURABLES } from 'data/measurables';
import { Measurable, WeekStartType } from 'data/measurables/types';
import {
  isSameMonth,
  isSameQuarter,
  isSameWeek,
  isSameDay,
  parseISO,
  isSameYear,
  format,
  endOfWeek,
  startOfWeek,
  subWeeks,
  subYears,
} from 'date-fns';
import { useRouter } from 'next/router';
import { getValueString } from 'pages/company/[company]/team/[team]/scoreboard/[measurable]';
import { Fragment, ReactElement, useState, useRef, useEffect } from 'react';
import { useMutation } from 'urql';
import { classNames, getScoreColor, refreshData } from 'utils';
import { CompanyUser } from 'components/UsersList/UsersList';
import { getVOPSValue } from 'components/UsersList/UsersList';
import { WGAssessment } from 'components';
import Image from 'next/image';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import DateTimeUtils from 'utils/datesAndTime';
import { logError } from 'utils/logger';
import button from 'styles/button';

const {
  countPerDynamicRange: dynamicRange,
  bucketizeScoresPerIntervals,
  getIntervalCounts,
  getBucketizedTotal,
} = DateTimeUtils;

type MeasurablesListProps = {
  measurables: Measurable[];
  measurableActions?: {
    label: string;
    action: (measurable: Measurable) => void;
  }[];
  editable?: boolean;
  isArchivedList?: boolean;
  users?: CompanyUser[];
  profileData?: { enabledWGProfile: boolean; enabledVOPSProfile: boolean };
  interval?: string;
  week_start_on?: number;
  selectedMeasurables?: number[];
  setSelectedMeasurables?;
  startDateProp?: string | Date;
  endDateProp?: string | Date;
};

const INTERVAL_COUNT = 6;

export default function MeasurablesList({
  measurables,
  measurableActions,
  editable,
  isArchivedList,
  users,
  profileData,
  interval,
  week_start_on,
  selectedMeasurables,
  setSelectedMeasurables,
  startDateProp,
  endDateProp,
}: MeasurablesListProps): ReactElement {
  const router = useRouter();
  const [sortedMeasurables, setSortedMeasurables] = useState(measurables);
  const [updatingSorting, setUpdatingSorting] = useState(false);
  const [editingScore, setEditingScore] = useState(false);
  const [scoreToEdit, setScoreToEdit] = useState(null);
  const [error, setError] = useState(null);
  const [updated, setUpdated] = useState(null);
  const [, updateMeasurable] = useMutation(UPDATE_MEASURABLE);
  const [, updateMeasurables] = useMutation(UPDATE_MEASURABLES);
  const [archiving, setArchiving] = useState<boolean>(false);
  const [showTrend, setShowTrend] = useState<boolean>(true);
  const [startDate, setStartDate] = useState<Date>(startDateProp ? new Date(startDateProp) : new Date());
  const [endDate, setEndDate] = useState<Date>(endDateProp ? new Date(endDateProp) : new Date());
  const [intervalCounts, setIntervalCounts] = useState({ daily: 0, weekly: 0, monthly: 0, quarterly: 0, annually: 0 });

  const measurableIds = measurables?.map(({ id }) => id) || [];
  const currentSelectedMeasurables = selectedMeasurables?.filter((id) => {
    return measurableIds.includes(id);
  });

  const columnStartDate = subWeeks(startOfWeek(new Date(), { weekStartsOn: week_start_on as WeekStartType }), 6);
  const columnEndDate = startOfWeek(new Date(), { weekStartsOn: week_start_on as WeekStartType });

  useEffect(() => {
    setSortedMeasurables(measurables);
  }, [measurables]);

  useEffect(() => {
    if (startDateProp) {
      setStartDate(new Date(startDateProp));
    }
  }, [startDateProp]);

  useEffect(() => {
    if (startDateProp) {
      setEndDate(new Date(endDateProp));
    }
  }, [endDateProp]);

  useEffect(() => {
    const days = dynamicRange('daily')(startDateProp, endDateProp);
    const weeks = dynamicRange('weekly')(startDateProp, endDateProp);
    const months = dynamicRange('monthly')(startDateProp, endDateProp);
    const quaters = dynamicRange('qauterly')(startDateProp, endDateProp);
    const years = Math.round(days / 365); // dynamicRange('annually')(startDateProp, endDateProp);

    if (endDateProp) {
      setEndDate(new Date(endDateProp));
      setIntervalCounts((prevState) => ({
        ...prevState,
        daily: days,
        weekly: weeks, //Math.abs(getWeek(new Date(startDateProp)) - getWeek(new Date(endDateProp))),
        monthly: months,
        quarterly: quaters,
        annually: years,
      }));
    }
  }, [startDateProp, endDateProp]);

  const intervals = Array(INTERVAL_COUNT)
    .fill(null)
    .map((_, i) =>
      getIntervalDate(interval as string, i, isNaN(new Date(endDate).getTime()) ? new Date() : endDate, week_start_on),
    );

  const dataColumns = intervals.map(({ start, strVal }) => (
    <th
      key={start.toString()}
      scope="col"
      className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider"
    >
      {strVal}
    </th>
  ));

  const data = (measurable: Measurable) => {
    let isSameFn;
    let customEndForAnnualScore;
    switch (measurable.interval) {
      case 'monthly':
        isSameFn = isSameMonth;
        break;
      case 'quarterly':
        isSameFn = isSameQuarter;
        break;
      case 'annually':
        isSameFn = isSameYear;
        customEndForAnnualScore = new Date('2000-01-01');
        break;
      case 'daily':
        isSameFn = isSameDay;
        break;
      case 'weekly':
      default:
        isSameFn = isSameWeek;
        break;
    }
    return intervals.map(({ start, strStart }) => {
      const _intervalCount = 7;
      // column count is fixed no need to use dynamic intervals as prev requirement
      // getIntervalCounts({
      //   start: measurable.interval === 'annually' ? customEndForAnnualScore : columnStartDate,
      //   end: columnEndDate,
      //   intervalType: measurable.interval,
      //   week_start_on,
      // });
      const scores = bucketizeScoresPerIntervals(measurable, _intervalCount, new Date(endDate), week_start_on);
      const score = scores.find(({ date }) => {
        return date && isSameFn(parseISO(date), parseISO(strStart), { weekStartsOn: week_start_on });
      });
      const isInSelectedDuration =
        new Date(strStart).getTime() >= new Date(startDateProp).getTime() &&
        new Date(strStart).getTime() <= new Date(endDateProp).getTime();

      const color = getScoreColor(
        score?.value,
        measurable.goal,
        measurable.conditional,
        measurable?.min_range,
        measurable?.max_range,
        measurable?.type,
      );

      return (
        <td
          key={start.toString()}
          className={`h-12 px-6 whitespace-nowrap ${color.bg}`}
          onClick={
            editable
              ? (event) => {
                  event.stopPropagation();

                  if (isInSelectedDuration) {
                    setEditingScore(true);
                    setScoreToEdit({ score: score, measurable, date: start });
                  }
                }
              : null
          }
        >
          {/* ccc */}
          <div className={`flex items-center justify-end ${color.fg} ${!isInSelectedDuration && 'text-gray-300'}`}>
            {score?.value || score?.value === 0 ? (
              measurable.type === 'boolean' ? (
                { true: 'Yes', false: 'No' }[String(Boolean(score?.value))]
              ) : (
                score.value?.toLocaleString()
              )
            ) : editable ? (
              <PlusIcon className={`h-4 w-4 stroke-2 ${!isInSelectedDuration && 'text-gray-300'}`} />
            ) : (
              ''
            )}
          </div>
        </td>
      );
    });
  };

  const [showPopover, setShowPopover] = useState<number>();
  const buttonRef = useRef(null);

  const dragMeasurable = useRef<number>(0);
  const draggedOverMeasurable = useRef<number>(0);

  const handleMeasurableSort = () => {
    setUpdatingSorting(true);
    const measurablesClone = [...sortedMeasurables];
    // following is replacement sort logic - not correct
    // const temp = measurablesClone[dragMeasurable.current];
    // measurablesClone[dragMeasurable.current] = measurablesClone[draggedOverMeasurable.current];
    // measurablesClone[draggedOverMeasurable.current] = temp;

    // following is insertion sort logic - which is what client wanted
    const temp = measurablesClone[dragMeasurable.current];
    measurablesClone.splice(dragMeasurable.current, 1);
    measurablesClone.splice(draggedOverMeasurable.current, 0, temp);

    setSortedMeasurables(measurablesClone);
    // TODO: update measurable order after updating
    measurablesClone.map(async (measurable, index) => {
      const { error: editError } = await updateMeasurable({
        _set: {
          sort_order: index,
        },
        id: measurable.id,
      });
      if (!editError) {
        setUpdatingSorting(false);
      }
    });
  };

  return (
    <>
      <div className="h-full">
        <div className="align-middle inline-block min-w-full h-full">
          <div className="border-b border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th></th>
                  {editable ? (
                    <th scope="col" className="pl- w-8">
                      <input
                        type="checkbox"
                        checked={currentSelectedMeasurables.length === measurables.length}
                        className="rounded cursor-pointer"
                        onChange={(event) => {
                          if (event.target.checked) {
                            setSelectedMeasurables(selectedMeasurables.concat(measurables.map(({ id }) => id)));
                          } else {
                            setSelectedMeasurables(selectedMeasurables.filter((_id) => !measurableIds?.includes(_id)));
                          }
                        }}
                      ></input>
                    </th>
                  ) : null}
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                  >
                    Owner
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                  >
                    Title
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                  >
                    Goal
                  </th>
                  {showTrend ? (
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                      onClick={() => setShowTrend(false)}
                    >
                      Trend <span className="font-bold text-black whitespace-nowrap">&gt;&lt;</span>
                    </th>
                  ) : (
                    <th onClick={() => setShowTrend(true)}>&lt;&gt;</th>
                  )}

                  {dataColumns}
                  <th scope="col" className="relative px-6 py-3 w-16">
                    <span className="sr-only">Edit</span>
                  </th>
                </tr>
              </thead>
              {updatingSorting ? (
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td colSpan={8} className="h-12 px-6 whitespace-nowrap">
                      <Loader color="text-black" />
                    </td>
                  </tr>
                </tbody>
              ) : (
                <tbody className="divide-y divide-gray-200">
                  {sortedMeasurables.map((measurable, index) => {
                    const { id, title, conditional, trend, type, goal, user, min_range, max_range } = measurable;
                    const wgSkills = users.find((_user) => _user.user_id === user?.id)?.wg_skills;
                    const vopsSkills = users.find((_user) => _user.user_id === user?.id)?.vops_skills;
                    const vopsValue = getVOPSValue(vopsSkills);
                    const _intervalCount = getIntervalCounts({
                      start: startDate,
                      end:
                        measurable.interval === 'weekly'
                          ? endOfWeek(new Date(endDate), { weekStartsOn: week_start_on as WeekStartType })
                          : endDate,
                      intervalType: measurable.interval,
                      week_start_on,
                    });
                    const total = getBucketizedTotal(
                      measurable,
                      _intervalCount,
                      measurable.interval === 'weekly'
                        ? endOfWeek(new Date(endDate), { weekStartsOn: week_start_on as WeekStartType })
                        : endDate,
                      week_start_on,
                    ); //getAverage(measurable, intervalCounts, measurable.interval);

                    // Get score count for selected date ranges
                    const scoureCountPerPeriod =
                      measurable.type === 'boolean'
                        ? bucketizeScoresPerIntervals(
                            measurable,
                            _intervalCount,
                            measurable.interval === 'weekly'
                              ? endOfWeek(new Date(endDate), { weekStartsOn: week_start_on as WeekStartType })
                              : endDate,
                            week_start_on,
                          )?.filter((score) => !isNaN(score.value)) || []
                        : bucketizeScoresPerIntervals(
                            measurable,
                            _intervalCount,
                            measurable.interval === 'weekly'
                              ? endOfWeek(new Date(endDate), { weekStartsOn: week_start_on as WeekStartType })
                              : endDate,
                            week_start_on,
                          )?.filter((score) => score.value !== null && score.value !== undefined) || [];

                    const average = total ? total / (scoureCountPerPeriod.length || 1) : 0;
                    const min = getValueString(min_range, type);
                    const max = getValueString(max_range, type);
                    const goalRange = min && max ? min + ' - ' + max : null;

                    let trendValue;
                    switch (trend) {
                      case 'total':
                        trendValue = measurable.scores.length
                          ? getBucketizedTotal(
                              measurable,
                              _intervalCount,
                              measurable.interval === 'weekly'
                                ? endOfWeek(new Date(endDate), { weekStartsOn: week_start_on as WeekStartType })
                                : endDate,
                              week_start_on,
                            )
                          : null;
                        break;
                      case 'average':
                      default:
                        trendValue =
                          measurable.type === 'boolean' && measurable.trend === 'average'
                            ? average
                            : measurable.scores.length
                            ? average.toFixed(2)
                            : null;
                        break;
                    }

                    const color = getScoreColor(
                      trendValue,
                      measurable.goal,
                      measurable.conditional,
                      measurable?.min_range,
                      measurable?.max_range,
                      measurable?.type,
                    );
                    return (
                      <tr
                        key={id}
                        className="cursor-pointer h-12"
                        onClick={() =>
                          router.push({
                            pathname: `/company/${router.query.company}/team/${router.query.team}/scoreboard/${id}`,
                            query: {
                              ...router.query,
                              startDate:
                                measurable.interval === 'annually'
                                  ? format(subYears(new Date(), 10), 'yyyy-MM-dd')
                                  : measurable.interval === 'quarterly'
                                  ? format(subYears(new Date(), 2), 'yyyy-MM-dd')
                                  : measurable.interval === 'monthly'
                                  ? format(subYears(new Date(), 1), 'yyyy-MM-dd')
                                  : format(startDate, 'yyyy-MM-dd'),
                              endDate: format(endDate, 'yyyy-MM-dd'),
                            },
                          })
                        }
                        draggable
                        onDragStart={() => (dragMeasurable.current = index)}
                        onDragEnter={() => (draggedOverMeasurable.current = index)}
                        onDragEnd={handleMeasurableSort}
                        onDragOver={(e) => e.preventDefault()}
                      >
                        <td>
                          <Image
                            src="/images/move-icons/drag.png"
                            className="-z-1 ml-3"
                            alt="New Feature"
                            height={20}
                            width={20}
                            style={{
                              maxWidth: '100%',
                              height: 'auto',
                            }}
                          />
                        </td>
                        {editable ? (
                          <td className="pl-4 w-8 py-3">
                            <input
                              type="checkbox"
                              checked={currentSelectedMeasurables?.includes(id)}
                              className="rounded cursor-pointer"
                              onClick={(event) => {
                                event.stopPropagation();
                              }}
                              onChange={(event) => {
                                if (event.target.checked) {
                                  setSelectedMeasurables([...selectedMeasurables, id]);
                                } else {
                                  setSelectedMeasurables(selectedMeasurables.filter((_id) => _id !== id));
                                }
                              }}
                            ></input>
                          </td>
                        ) : null}
                        <td>
                          <div className="flex whitespace-nowrap">
                            {user ? (
                              <>
                                {(profileData?.enabledWGProfile && wgSkills) ||
                                (profileData?.enabledVOPSProfile && vopsValue) ? (
                                  <Popover className="relative">
                                    <div>
                                      <Popover.Button
                                        ref={buttonRef}
                                        onMouseEnter={() => {
                                          setShowPopover(id);
                                        }}
                                        onMouseLeave={() => setShowPopover(null)}
                                      >
                                        <Avatar user={user} showName={true} />
                                      </Popover.Button>
                                      <Transition
                                        show={showPopover === id}
                                        as={Fragment}
                                        enter="transition ease-out duration-100"
                                        enterFrom="transform opacity-0 scale-95"
                                        enterTo="transform opacity-100 scale-100"
                                        leave="transition ease-in duration-75"
                                        leaveFrom="transform opacity-100 scale-100"
                                        leaveTo="transform opacity-0 scale-95"
                                      >
                                        <Popover.Panel className="z-10 origin-top-right absolute left-2 mt-2 rounded-lg border-2 border-gray-400 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
                                          <div className="h-10 rounded-lg flex divide-x divide-black text-black">
                                            {profileData?.enabledWGProfile && wgSkills ? (
                                              <WGAssessment
                                                wgSkills={wgSkills}
                                                user={{ ...user, company_user_id: user.id }}
                                              />
                                            ) : null}
                                            {profileData?.enabledVOPSProfile && vopsValue ? (
                                              <div className="text-black font-bold my-2 text-xs">
                                                <div className="mx-2 my-1">{vopsValue}</div>
                                              </div>
                                            ) : null}
                                          </div>
                                        </Popover.Panel>
                                      </Transition>
                                    </div>
                                  </Popover>
                                ) : (
                                  <Avatar user={user} showName={true} />
                                )}
                              </>
                            ) : null}
                          </div>
                        </td>
                        <td className="h-12 px-3 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="truncate w-32 sm:w-10 md:w-96 lg:w-96 xl:w-[30rem] 2xl:w-[30rem] 3xl:w-[30rem]">
                              {title}
                            </div>
                          </div>
                        </td>
                        <td className="h-12 px-6 whitespace-nowrap">
                          <div className="flex items-center">
                            {conditional && conditional !== 'range' ? (
                              <span className="mr-1">{getConditional(conditional)?.label}</span>
                            ) : null}
                            {conditional !== 'range' ? getValueString(goal, type) : goalRange}
                          </div>
                        </td>
                        {showTrend ? (
                          <td className={`h-12 px-0 whitespace-nowrap ${color.bg}`}>
                            <div className={`flex items-center justify-center ${color.fg}`}>
                              <div className="flex items-end gap-1">
                                {measurable.type === 'boolean'
                                  ? `${getValueString(
                                      trendValue * (measurable.trend === 'average' ? 100 : 1),
                                      measurable.trend === 'average' ? 'percentage' : type,
                                    )}`
                                  : getValueString(trendValue, type)}
                                {trendValue && (
                                  <span className="text-xs opacity-70 relative -top-1 text-[0.7rem] font-bold">
                                    {measurable.trend === 'total' ? 'TOT' : 'AVG'}
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                        ) : (
                          <td></td>
                        )}

                        {data(measurable)}
                        <td className="h-12 w-16 whitespace-nowrap text-sm text-gray-500 flex border-l border-gray-200">
                          {editable ? (
                            <Menu as="div" className="flex items-center justify-center w-full text-left">
                              {({ open }) => (
                                <>
                                  <div
                                    className="w-full h-full"
                                    onClick={(event) => {
                                      // Prevents redirecting to single page for item when clicking dots to close dropdown
                                      event.stopPropagation();
                                    }}
                                  >
                                    <Menu.Button className="flex items-center justify-center h-full w-full">
                                      <span className="sr-only">Open options</span>
                                      <DotsVerticalIcon className="h-5 w-5" aria-hidden="true" />
                                    </Menu.Button>
                                  </div>

                                  <Transition
                                    show={open}
                                    as={Fragment}
                                    enter="transition ease-out duration-100"
                                    enterFrom="transform opacity-0 scale-95"
                                    enterTo="transform opacity-100 scale-100"
                                    leave="transition ease-in duration-75"
                                    leaveFrom="transform opacity-100 scale-100"
                                    leaveTo="transform opacity-0 scale-95"
                                  >
                                    <Menu.Items
                                      static
                                      className="z-10 origin-top-right absolute right-0 top-10 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none"
                                    >
                                      <div className="py-1">
                                        {measurableActions.map(({ label, action }) => (
                                          <Menu.Item key={`${measurable.id}-${label}`}>
                                            {({ active }) => (
                                              <button
                                                type="button"
                                                className={classNames(
                                                  active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                                                  'block px-4 py-2 text-sm w-full text-left',
                                                )}
                                                onClick={async (event) => {
                                                  event.stopPropagation();

                                                  action?.(measurable);
                                                }}
                                              >
                                                {label}
                                              </button>
                                            )}
                                          </Menu.Item>
                                        ))}
                                      </div>
                                    </Menu.Items>
                                  </Transition>
                                </>
                              )}
                            </Menu>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              )}
            </table>
          </div>
        </div>
      </div>
      {selectedMeasurables.length ? (
        <FloatingActionButtons hpos="center" vpos="bottom">
          <FloatingActionButton
            onClick={async () => {
              if (archiving) return;

              setArchiving(true);
              const { error: updateError } = await updateMeasurables({
                ids: selectedMeasurables,
                _set: {
                  archived: isArchivedList ? false : true,
                },
              });

              if (updateError) {
                setError(updateError);
                logError({
                  error: updateError,
                  context: { component: 'MeasurableList' },
                });
              } else {
                await refreshData(router);
              }

              setSelectedMeasurables([]);
              setArchiving(false);
            }}
          >
            <div className="flex items-center justify-center w-14">
              {archiving ? <Loader color="text-white" /> : isArchivedList ? 'Unarchive' : 'Archive'}
            </div>
          </FloatingActionButton>
        </FloatingActionButtons>
      ) : null}
      <ErrorToast error={error} setError={setError} />
      <SuccessToast message={updated} setMessage={setUpdated} />
      <NewScoreDialog
        open={editingScore}
        setOpen={setEditingScore}
        score={scoreToEdit}
        week_start_on={week_start_on}
        setError={setError}
        setUpdated={setUpdated}
      />
    </>
  );
}
